import React, { useContext, useEffect } from "react";
import { secured } from "react-abac";
import mixpanel from "mixpanel-browser";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum, CRUDActionEnum, InsightsReportScheduleTypeEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import RestrictedSection from "~/components/RestrictedSection";
import { Alert, Avatar, Badge, Box, Button, Card, Dropdown, Grid, Icon, InputText, Modal, Section, Spinner, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import CoreContext from "~/lib/context";
import { useDispatch, useSelector } from "react-redux";
import Markdown from "~/components/Markdown";
import styled, { useTheme } from "styled-components";
import useAbacExt from "~/hooks/abac";
import { useHistory } from "react-router-dom";
import memberInsightsSelectors from "~/store/memberInsights/selectors";
import memberInsightsActionCreators from "~/store/memberInsights/actions";
import MemberInsightsReportUpdateModal from "~/components/MemberInsightsReportUpdateModal";
import MemberInsightsReportDeleteModal from "~/components/MemberInsightsReportDeleteModal";
import moment from "moment";
import lists from "~/lib/lists";
import StyledIcon from "~/components/StyledIcon";
const Cap = styled.div`
  display: inline-block;
  width: ${_ref => {
  let {
    theme
  } = _ref;
  return 6 * theme.typography.fontStep;
}}rem;
  min-width: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return 6 * theme.typography.fontStep;
}}rem;
  height: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return 6 * theme.typography.fontStep;
}}rem;
  text-align: center;
  line-height: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return 6 * theme.typography.fontStep;
}}rem;
  border-radius: 50%;
  background-color: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.colors.primary.light;
}};
  position: relative;
  font-weight: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.typography.fontWeight.semiBold;
}};
  font-size: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return 3 * theme.typography.fontStep;
}}rem;
  color: ${_ref8 => {
  let {
    theme
  } = _ref8;
  return theme.colors.primary.main;
}};
  box-shadow: 0 0 0 2px ${_ref9 => {
  let {
    theme
  } = _ref9;
  return theme.colors.background.primary;
}};
`;
const StyledAvatar = styled(Avatar)`
  box-shadow: 0 0 0 2px ${_ref10 => {
  let {
    theme
  } = _ref10;
  return theme.colors.background.primary;
}};
`;
const MemberInsightsReports = () => {
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const configs = useSelector(memberInsightsSelectors.getConfigs);
  const action = useSelector(memberInsightsSelectors.getAction);
  const status = useSelector(memberInsightsSelectors.getConfigsStatus);
  const error = useSelector(memberInsightsSelectors.getConfigsError);
  const [query, setQuery] = React.useState("");
  const {
    setSidebar
  } = useContext(CoreContext);
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    userHas
  } = useAbacExt();
  let filteredConfigs = configs;
  if (query) {
    filteredConfigs = configs.filter(config => config.name.toLowerCase().includes(query.toLowerCase()));
  }
  useEffect(() => {
    setSidebar(undefined);
  }, [setSidebar]);
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(memberInsightsActionCreators.fetchConfigsStarted());
    }
  }, [status]);
  const handleView = id => {
    history.push(RoutesEnum.MEMBER_INSIGHTS_CONFIG.replace(":configId", id));
    mixpanel.track(`Member Insight Config – View`, {
      configId: id
    });
  };
  const handleEdit = id => {
    dispatch(memberInsightsActionCreators.actionChanged(CRUDActionEnum.UPDATE, id));
    mixpanel.track(`Member Insight Config – Edit`, {
      configId: id
    });
  };
  const handleDelete = id => {
    dispatch(memberInsightsActionCreators.actionChanged(CRUDActionEnum.DELETE, id));
    mixpanel.track(`Member Insight Config – Delete`, {
      configId: id
    });
  };
  const handleExplorerClick = () => {
    history.push(RoutesEnum.MEMBER_INSIGHTS_EXPLORER);
  };
  if (!configs.length && status === StatusStateEnum.SUCCEEDED) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Text, {
      element: "h1",
      s: {
        mt: 5,
        mb: 8
      },
      className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
    }, t("MemberInsightsReports.memberInsightsReports")), /*#__PURE__*/React.createElement(Grid, {
      cols: 1,
      justify: "center",
      s: {
        mt: 16
      }
    }, /*#__PURE__*/React.createElement(StyledIcon, {
      src: "document",
      color: "translucentInfo"
    }), /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      weight: "semiBold",
      display: "block",
      s: {
        mt: 6
      },
      align: "center",
      style: {
        maxWidth: "400px"
      }
    }, t("MemberInsightsReports.noReports")), /*#__PURE__*/React.createElement(Markdown, {
      color: "textSecondary",
      fontSize: 4,
      display: "block",
      s: {
        mt: 3,
        mb: 5
      },
      lineHeight: 6,
      style: {
        maxWidth: "400px"
      },
      align: "center",
      text: t("MemberInsightsReports.noReportsInfo")
    }), /*#__PURE__*/React.createElement(Button, {
      color: "primary",
      onClick: () => handleExplorerClick()
    }, t("MemberInsightsReports.contributionExplorer"))));
  }
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    s: {
      mt: 3,
      mb: 8
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, t("MemberInsightsReports.memberInsightsReports")), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: error || "",
    linkColor: "text"
  })), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 5,
    template: "1fr",
    s: {
      mb: 5
    }
  }, /*#__PURE__*/React.createElement(InputText, {
    value: query,
    onChange: val => setQuery(val),
    autoComplete: "off",
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "search",
      s: {
        mr: 2
      }
    }),
    placeholder: t("MemberInsightsReports.search"),
    clearButton: true,
    fullWidth: true
  })), !filteredConfigs?.length && status === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
    s: {
      mt: 8
    },
    center: true
  }) : filteredConfigs?.length ? /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    gap: 5,
    equal: true
  }, filteredConfigs.map(config => /*#__PURE__*/React.createElement(Card, {
    key: config.id,
    color: "background",
    onClick: () => handleView(config.id),
    shadow: true
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 5,
    gap: 3,
    template: "1fr auto auto auto auto",
    align: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h3"
  }, config.name), config.members.length ? /*#__PURE__*/React.createElement(Box, {
    direction: "row"
  }, config.members.slice(0, 5).map(member => /*#__PURE__*/React.createElement(StyledAvatar, {
    color: member.isTemporary ? theme.colors.borders.secondary : theme.colors.primary.main,
    s: {
      mr: -1
    },
    size: 6,
    user: member,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": `${member.firstName} ${member.lastName}${member.email ? `(${member.email})` : ""}`
  })), config.members.length > 5 && /*#__PURE__*/React.createElement(Cap, null, "+", config.members.length - 5)) : /*#__PURE__*/React.createElement(Badge, {
    color: "translucentPrimary"
  }, "All Members"), /*#__PURE__*/React.createElement(Badge, {
    color: "translucentPrimary",
    s: {
      px: 3
    }
  }, lists.memberInsights.scheduleTypes.names[config.scheduleType], config.scheduleType === InsightsReportScheduleTypeEnum.WEEKLY ? ` on ${moment().weekday(config.scheduleDayOfWeek - 1).format("ddd")}` : "", " ", "at ", moment.utc(config.scheduleTimeOfDay, "hh:mma").local().format("hh:mma")), /*#__PURE__*/React.createElement(Badge, {
    color: "translucentPrimary",
    s: {
      px: 3
    }
  }, lists.memberInsights.scheduleDestinations.names[config.scheduleDestination]), /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    handlerProps: {
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref11 => {
    let {
      isOpen,
      onItemClick
    } = _ref11;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHas([AuthoritiesEnum.MEMBER_INSIGHTS_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleEdit(config.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "pencil",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHas([AuthoritiesEnum.MEMBER_INSIGHTS_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDelete(config.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "bin",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  }))))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 5,
    align: "center",
    s: {
      mt: 8
    },
    display: "block"
  }, t("MemberInsightsReports.noReportsFound")), userHas(AuthoritiesEnum.MEMBER_INSIGHTS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(MemberInsightsReportUpdateModal, null)), userHas(AuthoritiesEnum.MEMBER_INSIGHTS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.DELETE
  }, /*#__PURE__*/React.createElement(MemberInsightsReportDeleteModal, null)));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.MEMBER_INSIGHTS_WRITE,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(MemberInsightsReports), () => true);